import {Tooltip} from "primereact/tooltip";
import {Fragment, useEffect, useRef, useState} from "react";
import {shallowEqual, useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared";
import {formatParameterValue, formatValueRange} from "@reva/shared/number-format";

import {useRevaTranslation} from "../../hooks/languageHooks";
import {selectHasSubtabParams} from "../../redux/objectParametersSelectors";
import {selectCommentForParam} from "../../redux/objectParametersSlice";
import {selectRangesForApiParamCurried, selectValueTooltipSubtabSpecParams} from "../../redux/specSelectors";
import {truncateLinesAndText} from "../../util/stringUtil";
import {getResourceColor} from "./paramHooks";

/** Tooltip row key → parameter type of the layers that carry comments. */
const COMMENT_LAYERS = {
    reva: "AVM",
    register: "REGISTER",
    energy: "ENERGY_CERT",
    plot: "PLOT",
    inspectionDerived: "INSPECTION_DERIVED",
};

export default function ValuesIconDefault({
    parameter_details_def,
    allValues,
    tooltipRef,
    label,
    associatedOutputParamValue,
}) {
    const ref = useRef(null);

    const [mounted, setMounted] = useState(false);
    const {t, language} = useRevaTranslation();
    const hasSubtabParams = useSelector(selectHasSubtabParams);
    const subtabSpecParams = useSelector(selectValueTooltipSubtabSpecParams);
    const hasInspectionDerivedValue = allValues?.inspectionDerived != null;
    const hasInspection = allValues?.inspection != null;
    const parameter = parameter_details_def?.parameter;
    const ranges = useSelector(selectRangesForApiParamCurried(parameter));
    // #19774: comments of every layer that has one, keyed like the tooltip rows
    const comments = useSelector(
        (state) =>
            parameter
                ? Object.fromEntries(
                      Object.entries(COMMENT_LAYERS)
                          .map(([key, parameterType]) => [key, selectCommentForParam(state, parameter, parameterType)])
                          .filter(([, comment]) => comment),
                  )
                : {},
        shallowEqual,
    );
    const keys = [
        "reva",
        "firstFinalize",
        "register",
        "energy",
        "idpEnergyCertificate",
        "plot",
        "inspectionDerived",
        "inspection",
        "initialValuation",
        "immodrop",
    ]
        // a layer with a comment always keeps its row, so the comment is never hidden
        .filter((key) => hasSubtabParams[key] || comments[key])
        .filter((key) => key !== "inspection" || hasInspection)
        .filter(
            (key) =>
                key !== "inspectionDerived" ||
                hasInspectionDerivedValue ||
                (!hasInspection && !hasInspectionDerivedValue),
        )
        .filter((key) => comments[key] || !parameter || !subtabSpecParams[key] || subtabSpecParams[key].has(parameter));
    const values = {...allValues};
    //#18566: register value overrides first finalize value for prop_leasehold parameters
    if (
        parameter_details_def?.parameter?.startsWith("prop_leasehold") &&
        values.register != null &&
        values.firstFinalize != null
    ) {
        values.valuationValue = values.register;
    }
    //#18566: inspectionDerived value overrides first finalize value for gross_floor_area
    if (
        parameter_details_def?.parameter === "gross_floor_area" &&
        values.inspectionDerived != null &&
        values.firstFinalize != null
    ) {
        values.valuationValue = values.inspectionDerived;
    }

    useEffect(() => {
        if (ref.current && !mounted) {
            setMounted(true); // Trigger a re-render when ref is assigned
        }
    }, [mounted]);
    const valueMapper = (value) => {
        if (parameter_details_def) {
            if (value === "-") return value; // Don't try to format placeholder
            const rangeLabel = ranges?.length ? formatValueRange(language, value, ranges) : null;
            if (rangeLabel != null) {
                const rawValue = formatParameterValue(language, parameter_details_def, value, {omitUnit: true});
                return `${rangeLabel} (${rawValue})`;
            }
            const options =
                parameter_details_def.parameter === "ownership_share" ? {formatOverride: "DOUBLE(0-4)"} : undefined;
            let formatedValue = formatParameterValue(language, parameter_details_def, value, options);
            if (
                parameter_details_def.parameter === "building_type" &&
                formatedValue != null &&
                (formatedValue.includes("(ungültig)") || formatedValue.includes("(invalid)"))
            )
                formatedValue = selectByLanguage(
                    language,
                    "Wert passt nicht zum gewähltem Segment",
                    "Value does not match selected segment",
                );
            return !formatedValue || formatedValue === "NaN" ? "-" : formatedValue;
        }

        if (value === "empty") {
            return t("object.valueTooltip.revaEmpty");
        }
        return value ?? "-";
    };

    const valuationValue = valueMapper(values?.valuationValue);
    const getColor = (key) => `text-${getResourceColor(key, false, true)}`;
    if (associatedOutputParamValue != null && values?.valuationValue === "-") {
        keys.push("fallback");
        values.fallback = associatedOutputParamValue;
    }

    return (
        <>
            <Tooltip target={`.${tooltipRef}`} className="custom-tooltip">
                <p className="mt-0">{t("object.valueTooltip.title") + label}</p>
                <div className="grid grid-cols-[auto_1fr] gap-x-4">
                    <b>{t("object.valueTooltip.valuation")}:</b>
                    <b>{valuationValue}</b>
                    {keys.map((key) => {
                        const value = valueMapper(values?.[key]);
                        const Cell = value === valuationValue ? "b" : "span";
                        return (
                            <Fragment key={key}>
                                <Cell className={getColor(key)}>{t(`object.valueTooltip.${key}`)}:</Cell>
                                <Cell className={getColor(key)}>{value}</Cell>
                                {comments[key] && (
                                    // #19774: the layer's comment right below its value, in the value column
                                    <span
                                        className={`col-start-2 italic whitespace-pre-line line-clamp-3 ${getColor(key)}`}
                                    >
                                        {truncateLinesAndText(comments[key])}
                                    </span>
                                )}
                            </Fragment>
                        );
                    })}
                </div>
            </Tooltip>
            <i
                ref={ref}
                className={`pi pi-info-circle tooltip-icon text-blue-500 ${tooltipRef}`}
                data-pr-position="top"
            />
        </>
    );
}
